import React, { useEffect, useState, useCallback } from "react";
// FIX: Added FaEye to the imports
import { FaDownload, FaCalendarAlt, FaEye } from "react-icons/fa";
import SummaryApi from "../common"; 
import { useAuth } from "../helpers/AuthContext";
import nocertification from "../asset/nocertification.png";
import * as pdfjsLib from "pdfjs-dist";

// --- WORKER CONFIG ---
try {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
} catch (e) {
  console.warn("Worker init warning:", e);
}

const StudentCertificate = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [certificateData, setCertificateData] = useState({});
  const [thumbnails, setThumbnails] = useState({}); 
  const { studentId, user } = useAuth();

  // --- 1. DYNAMIC URL GENERATOR ---
  const getPdfUrl = (relativePath) => {
    if (!relativePath) return "";
    
    const cleanPath = relativePath.replace(/\\/g, '/');
    const apiBase = SummaryApi.Images.url; 
    const rootServerUrl = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;

    return `${rootServerUrl}/${cleanPath}`;
  };

  // --- 2. FETCH DATA ---
  const fetchPurchasedCourses = useCallback(async () => {
    if (!studentId) {
      setLoading(false); return;
    }
    try {
      const res = await fetch(SummaryApi.student_purchased_courses.url, {
        method: SummaryApi.student_purchased_courses.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_id: studentId }),
      });
      const data = await res.json();
      if (data.status && Array.isArray(data.data)) {
        setEnrollments(data.data);
        data.data.forEach(enrollment => fetchCertificateData(enrollment.enrollment_id));
        if (data.data.length === 0) setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error(error); setLoading(false);
    }
  }, [studentId]);

  const fetchCertificateData = async (enrollmentId) => {
    try {
      const response = await fetch(SummaryApi.certificate_enrollment_retrieve_by_id.url, {
        method: SummaryApi.certificate_enrollment_retrieve_by_id.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enrollment_id: parseInt(enrollmentId) }),
      });
      const data = await response.json();
      if (data.status) {
        setCertificateData(prev => ({ ...prev, [enrollmentId]: data.data }));
        if (data.data?.is_generate_certificate === 1 && data.data?.certificate_path) {
          generatePdfPreview(enrollmentId, data.data.certificate_path);
        }
      }
    } catch (error) { console.error(error); }
  };

  // --- 3. GENERATE PREVIEW IMAGE ---
  const generatePdfPreview = async (enrollmentId, relativePath) => {
    try {
      setThumbnails(prev => ({ ...prev, [enrollmentId]: 'LOADING' }));
      const pdfUrl = getPdfUrl(relativePath);

      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("CORS Blocked");
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const loadingTask = pdfjsLib.getDocument(blobUrl);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      
      const viewport = page.getViewport({ scale: 0.6 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      await page.render({ canvasContext: context, viewport }).promise;
      
      setThumbnails(prev => ({ ...prev, [enrollmentId]: canvas.toDataURL("image/png") }));

    } catch (error) {
      setThumbnails(prev => ({ ...prev, [enrollmentId]: 'FAILED' }));
    }
  };

  // --- 4. HANDLE DOWNLOAD (Silent Blob Method) ---
  const handleDownload = async (courseTitle, certPath) => {
    if (!certPath) return;

    try {
      const pdfUrl = getPdfUrl(certPath);
      const cleanTitle = courseTitle.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${cleanTitle}_Certificate.pdf`;

      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("CORS/Network Error");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName; 
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

    } catch (error) {
      console.error("Download failed:", error);
      alert("Download failed. Please check Backend CORS settings.");
    }
  };

  // --- MOCK CERTIFICATE COMPONENT ---
  const RealisticMockCertificate = ({ courseName, date }) => (
    <div className="w-full h-full bg-white relative overflow-hidden flex flex-col items-center justify-center shadow-sm border border-gray-200 select-none pointer-events-none">
      <div className="absolute top-0 left-0 w-[40%] h-[60%] bg-[#d4af37] rounded-br-[100%] z-0 opacity-80"></div>
      <div className="absolute top-0 left-0 w-[35%] h-[55%] bg-[#0f2d5c] rounded-br-[100%] z-10"></div>
      <div className="absolute bottom-0 right-0 w-[40%] h-[60%] bg-[#d4af37] rounded-tl-[100%] z-0 opacity-80"></div>
      <div className="absolute bottom-0 right-0 w-[35%] h-[55%] bg-[#0f2d5c] rounded-tl-[100%] z-10"></div>
      <div className="absolute top-1 right-2 text-[5px] font-bold text-[#0f2d5c] tracking-wider">SKILLARA</div>
      <div className="z-20 flex flex-col items-center w-[85%] text-center mt-1">
        <h1 className="text-[8px] font-bold text-[#0f2d5c] uppercase tracking-wider mb-[1px]">Certificate</h1>
        <div className="bg-[#d4af37] px-2 py-[0.5px] relative mb-1 shadow-sm">
          <span className="text-[3px] text-white font-bold uppercase tracking-wide">Of Appreciation</span>
        </div>
        <h2 className="text-[8px] text-[#1f4774] mb-[1px] font-serif italic">{user?.name || "Student Name"}</h2>
        <div className="w-8 h-[0.5px] bg-[#d4af37] mb-1"></div>
        <h3 className="text-[5px] font-bold text-black leading-tight px-1 line-clamp-2 uppercase">{courseName}</h3>
        <p className="text-[3px] text-gray-500 mt-1">Completed: {date}</p>
        <div className="mt-1 w-4 h-4 rounded-full bg-[#d4af37] flex items-center justify-center border border-white shadow-sm">
           <span className="text-[2px] text-white font-bold">BEST</span>
        </div>
      </div>
    </div>
  );

  useEffect(() => { fetchPurchasedCourses(); }, [fetchPurchasedCourses]);
  useEffect(() => {
    if (enrollments.length > 0 && Object.keys(certificateData).length >= enrollments.length) setLoading(false);
  }, [certificateData, enrollments]);

  if (loading) return <div className="p-6 min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1f4774] border-t-transparent"></div></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1f4774] dark:text-white mb-6 text-center">My Certificates</h1>
        {enrollments.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-screen">
             <img src={nocertification} alt="No Certificates" className="w-48 object-contain mb-6 opacity-80" />
             <p className="text-lg font-medium text-gray-700">No certificates available yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto shadow-md rounded-xl border border-gray-200 dark:border-gray-700">
    <table className="min-w-full bg-white dark:bg-gray-800 w-full text-center">

              <thead>
                <tr className="bg-[#1f4774] dark:bg-blue-700 text-white text-left text-sm font-semibold">
                                      <th className="px-6 py-4 text-center">S.No</th>
                  <th className="px-6 py-4 text-center">Course Name</th>
                  <th className="px-6 py-4 text-center">Enrolled Date</th>
                  <th className="px-6 py-4 text-center">Certificate</th>
         <th className="px-6 py-4 text-center">Certificate Issued On</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-gray-300 text-center">
                {enrollments.map((enrollment,index) => {
                  const certData = certificateData[enrollment.enrollment_id];
                  const hasCert = certData?.is_generate_certificate === 1;
                  const thumbStatus = thumbnails[enrollment.enrollment_id];
                  const certPath = certData?.certificate_path;
                  const formattedDate = new Date(enrollment.enrollment_date).toLocaleDateString("en-GB");

                  return (
                    <tr key={enrollment.enrollment_id} className="border-t border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-center">
                                             <td className="px-6 py-2 text-center font-semibold text-[#1f4774] dark:text-blue-400">
          {index + 1}
        </td>
                      <td className="px-6 align-middle py-2 text-center font-semibold text-[#1f4774] dark:text-blue-400">{enrollment.course_title}</td>
                      <td className="px-6 align-middle py-2 text-center">
                         <div className="flex align-middle items-center justify-center gap-1 text-sm">
                           <FaCalendarAlt className="text-[#1f4774] dark:text-blue-400" />
                           {formattedDate}
                         </div>
                      </td>
                      
            <td className="px-6 py-4 text-center">
  {hasCert ? (
    <div className="flex flex-col items-center gap-3">
      
      {/* PREVIEW IMAGE */}
      <div 
        onClick={() => handleDownload(enrollment.course_title, certPath)}
        className="relative align-middle w-40 h-28 cursor-pointer rounded-md overflow-hidden border text-center border-gray-300 hover:border-[#1f4774] hover:shadow-lg hover:scale-105 transition-all group bg-white"
        title="Click to Download PDF"
      >
        {thumbStatus === 'LOADING' ? (
          <div className="w-full h-full bg-gray-50 animate-pulse flex items-center justify-center text-[10px] text-gray-400">Generating...</div>
        ) : (thumbStatus === 'FAILED' || !thumbStatus) ? (
          <RealisticMockCertificate courseName={enrollment.course_title} date={formattedDate} />
        ) : (
          <img src={thumbStatus} alt="Certificate" className="w-full h-full object-cover" />
        )}
        
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
          <FaEye className="text-white opacity-0 group-hover:opacity-100 drop-shadow-md text-xl" />
        </div>
      </div>

      {/* DOWNLOAD BUTTON */}
      <button 
        onClick={() => handleDownload(enrollment.course_title, certPath)}
        className="flex items-center justify-center gap-2 bg-[#1f4774] hover:bg-blue-700 text-white text-sm font-semibold py-2.5 px-6 rounded-md shadow-md transition-all active:scale-95"
      >
        <FaDownload /> Download
      </button>

    </div>
  ) : (
    <div className="flex flex-col items-center justify-center h-full">
      {/* SVG Icon for No Certificate */}
      <svg 
        className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-2" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={1.5} 
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
        />
      </svg>
      <span className="text-xs text-gray-400 italic">Certificate not available</span>
    </div>
  )}
</td>


<td className="px-6 align-middle py-2 text-center">
  <div className="flex align-middle items-center justify-center gap-1 text-sm">
    <FaCalendarAlt className="text-[#1f4774] dark:text-blue-400" />
    {new Date(enrollment.course_completed_date).toLocaleDateString("en-GB")}
  </div>
</td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentCertificate;